// Typed fetch wrapper for the Pugio control-plane API.
//
// All paths are relative so the browser hits the same origin the dashboard
// was served from. In dev, Vite proxies /auth, /servers, /health, /orgs, /invites
// to the backend; in prod, nginx proxies the same prefixes.

import { getToken } from "./auth";
import { API_BASE, ApiError, clearUnauthorizedSession, request } from "./api/core";

export { API_BASE, ApiError, request } from "./api/core";
export {
    changeEmail,
    changePassword,
    deleteAccount,
    exportAccountData,
    forgotPassword,
    getNotificationPrefs,
    listOauthProviders,
    login,
    loginMfa,
    finishMfaChallenge,
    me,
    oauthStartUrl,
    resendVerification,
    resetPassword,
    signup,
    updateNotificationPrefs,
    verifyEmail,
} from "./api/auth";
export type {
    AccountExportResponse,
    Customer,
    LoginResponseWithMFA,
    NotificationPreference,
    NotificationPreferenceUpdate,
    OauthProvider,
    TokenResponse,
} from "./api/auth";
export {
    createCheckout,
    getBillingPortal,
    getBillingSummary,
    getSubscription,
} from "./api/billing";
export type {
    BillingInvoiceLine,
    BillingPlan,
    BillingSummaryResponse,
    CreateCheckoutResponse,
    PortalResponse,
    SubscriptionGateState,
    SubscriptionResponse,
} from "./api/billing";
export {
    listEndUserGrants,
    revokeEndUserGrant,
} from "./api/grants";
export type {
    EndUserGrantSummary,
    EndUserGrantsResponse,
    GrantsUrlParams,
} from "./api/grants";

export type ServerStatus = "pending" | "generating" | "building" | "running" | "failed";

export type AuthStrategyKind =
    | "auto"
    | "none"
    | "header_bearer"
    | "header_basic"
    | "header_custom"
    | "query"
    | "path"
    | "header_pair"
    | "query_pair"
    /** Pugio stores no upstream credential; the agent supplies it
     * in a configurable header on every MCP call. */
    | "passthrough"
    /** OAuth 2.0 M2M.  Pugio stores client_id + client_secret +
     * token_url; the container fetches + caches access tokens. */
    | "oauth_client_credentials";

export type OrgRole = "member" | "viewer" | "editor" | "admin" | "owner";
export type ToolMode = "direct" | "compact";

export interface ServerSummary {
    project_id?: string | null;
    id: string;
    slug: string;
    display_name: string;
    status: ServerStatus;
    port: number | null;
    url: string;
    auth_strategy: AuthStrategyKind | null;
    org_id: string | null;
    connection_id?: string | null;
    credential_mode?: "legacy" | "connection";
    ui_enabled: boolean;
    tool_mode: ToolMode;
    runtime_kind?: "shared" | "isolated";
    placement_preference?: "auto" | "required_isolation";
    placement_reason?: string;
    published_generation?: number | null;
    last_error: string | null;
    created_at: string;
    updated_at: string;
    // Public catalogue (Feature #4)
    public: boolean;
    public_description: string | null;
    public_tags: string | null;
    public_display_owner: string | null;
    // Mock-fixture mode: "off" (default) or "on".
    fixtures_mode?: "off" | "on";
    // Spec retention metadata (migration 0031).
    // ``persist`` keeps the uploaded spec on disk forever; ``ephemeral``
    // deletes it after the first deploy.  ``spec_path_present`` is
    // false on ephemeral servers post-first-deploy, telling the
    // dashboard to gate the regenerate button behind a fresh upload.
    // ``has_pending_spec`` flips on the "review & confirm" drawer.
    spec_retention?: SpecRetention;
    spec_path_present?: boolean;
    has_pending_spec?: boolean;
    pending_uploaded_at?: string | null;
    has_unapplied_changes?: boolean;
    unapplied_change_reasons?: string[];
    // Container runtime state surfaced on the list + detail
    // endpoints so the Overview tab can render the right Pause /
    // Resume button without an extra round trip to /status.
    // Optional for backward compatibility with stale backends.
    runtime_state?: "running" | "paused" | "stopped";
}

export interface ServerListItem extends ServerSummary {
    security_score: SecurityScoreResponse | null;
    pending_optimisation_count: number;
}

export type SpecRetention = "persist" | "ephemeral";

export interface ServerCreated {
    id: string;
    slug: string;
    display_name: string;
    status: ServerStatus;
    url: string;
    bearer_token: string;  // plaintext â€” only returned once at creation
    auth_strategy: AuthStrategyKind;
    tool_mode: ToolMode;
    org_id: string | null;
    created_at: string;
}

export interface OrgSummary {
    id: string;
    name: string;
    slug: string;
    created_at: string;
    member_count: number;
    my_role: OrgRole;
}

export interface OrgMember {
    customer_id: string;
    email: string;
    role: OrgRole;
    joined_at: string;
    granted_scopes: string[];
}

export interface OrgDetail {
    id: string;
    name: string;
    slug: string;
    created_by: string | null;
    created_at: string;
    my_role: OrgRole;
    members: OrgMember[];
    seats?: { limit: number | null; members: number; pending_invites: number; used: number;
        available: number; over_limit: boolean; can_invite: boolean; reason: string };
    // Feature #5 â€” compliance flags, defaulted for older backends.
    data_residency?: string;
    mfa_required?: boolean;
    saml_required?: boolean;
}

export interface OrgInvite {
    id: string;
    email: string;
    role: OrgRole;
    token: string;
    expires_at: string;
    created_at: string;
}

// ---------- Servers ----------

export function listServers(opts?: { limit?: number; offset?: number; workspace?: string; project_id?: string }): Promise<ServerSummary[]> {
    const q = new URLSearchParams();
    if (opts?.limit !== undefined) q.set("limit", String(opts.limit));
    if (opts?.offset !== undefined) q.set("offset", String(opts.offset));
    if (opts?.project_id) q.set("project_id", opts.project_id);
    if (opts && "workspace" in opts && opts.workspace) q.set("workspace", String(opts.workspace));
    const qs = q.toString();
    return request(`/servers${qs ? `?${qs}` : ""}`);
}

export function listServerSummaries(opts?: { limit?: number; offset?: number; workspace?: string; project_id?: string }): Promise<ServerListItem[]> {
    const q = new URLSearchParams();
    if (opts?.limit !== undefined) q.set("limit", String(opts.limit));
    if (opts?.offset !== undefined) q.set("offset", String(opts.offset));
    if (opts?.project_id) q.set("project_id", opts.project_id);
    if (opts && "workspace" in opts && opts.workspace) q.set("workspace", String(opts.workspace));
    const qs = q.toString();
    return request(`/servers/summary${qs ? `?${qs}` : ""}`);
}

export function getServer(id: string, signal?: AbortSignal): Promise<ServerSummary> {
    return request(`/servers/${id}`, { signal });
}

export interface ServerPlacement {
    preference: "auto" | "required_isolation";
    effective_runtime: "shared" | "isolated";
    publication_reason: string;
    published_generation: number | null;
    shared_eligible: boolean | null;
    eligibility_status: "eligible" | "ineligible" | "unknown";
    eligibility_reasons: string[];
}

export function getServerPlacement(id: string, signal?: AbortSignal): Promise<ServerPlacement> {
    return request(`/servers/${id}/placement`, { signal });
}

export interface RuntimeMigrationStatus {
    job: { id: string; generation: number; state: string; stage: string; error_code: string | null } | null;
    rollback_targets: { fence: number; generation: number | null; runtime_kind: "shared" | "isolated"; retained_until: string }[];
}

export function getRuntimeMigration(id: string, signal?: AbortSignal): Promise<RuntimeMigrationStatus> {
    return request(`/servers/${id}/migration`, { signal });
}

export function migrateRuntime(id: string, target: "shared" | "isolated", rollback_fence?: number): Promise<{ job_id: string }> {
    return request(`/servers/${id}/migration`, { method: "POST", body: JSON.stringify({ target, rollback_fence }) });
}

export function createServer(params: {
    slug: string;
    display_name: string;
    target_base_url: string;
    spec: File;
    target_api_token?: string;
    target_api_secondary_token?: string;
    auth_strategy?: AuthStrategyKind;
    org_id?: string;
    project_id?: string;
    connection_id?: string;
    expected_connection_revision?: number;
    /** Optional override for ``passthrough`` strategy.  Defaults
     * server-side to ``X-Upstream-Authorization``. */
    passthrough_header?: string;
    /** Required when ``auth_strategy === "oauth_client_credentials"``. */
    oauth_token_url?: string;
    /** Optional JSON object of extra body params for the OAuth token
     * POST (audience / scope / resource).  Only honoured for
     * ``oauth_client_credentials``. */
    oauth_extra_params?: string;
    /** Spec retention mode (default ``"persist"``).  ``"ephemeral"``
     * deletes the uploaded spec immediately after the first deploy
     * succeeds â€” privacy-minded customers with sensitive internal
     * specs they don't want stored on the control plane. */
    spec_retention?: SpecRetention;
    tool_mode?: ToolMode;
}): Promise<ServerCreated> {
    const form = new FormData();
    form.set("slug", params.slug);
    form.set("display_name", params.display_name);
    form.set("target_base_url", params.target_base_url);
    form.set("spec", params.spec);
    if (params.target_api_token) form.set("target_api_token", params.target_api_token);
    if (params.target_api_secondary_token) form.set("target_api_secondary_token", params.target_api_secondary_token);
    if (params.auth_strategy) form.set("auth_strategy", params.auth_strategy);
    if (params.org_id) form.set("org_id", params.org_id);
    if (params.project_id) form.set("project_id", params.project_id);
    if (params.connection_id) form.set("connection_id", params.connection_id);
    if (params.expected_connection_revision !== undefined) form.set("expected_connection_revision", String(params.expected_connection_revision));
    if (params.passthrough_header) form.set("passthrough_header", params.passthrough_header);
    if (params.oauth_token_url) form.set("oauth_token_url", params.oauth_token_url);
    if (params.oauth_extra_params) form.set("oauth_extra_params", params.oauth_extra_params);
    if (params.spec_retention) form.set("spec_retention", params.spec_retention);
    if (params.tool_mode) form.set("tool_mode", params.tool_mode);
    return request("/servers", { method: "POST", body: form });
}

export function deleteServer(id: string): Promise<void> {
    return request(`/servers/${id}`, { method: "DELETE" });
}

export function regenerateServer(id: string, expectedReviewDigest?: string): Promise<ServerSummary> {
    return request(`/servers/${id}/regenerate${expectedReviewDigest ? "?expected_review_digest=" + encodeURIComponent(expectedReviewDigest) : ""}`, { method: "POST" });
}

// ---------- Credential rotation ----------

export interface ServerCredentialsPatch {
    /** Plaintext primary target-API token.  Empty string = clear. */
    target_api_token?: string | null;
    /** Plaintext secondary token.  Used by ``header_pair`` /
     * ``query_pair`` strategies only. */
    target_api_secondary_token?: string | null;
}

/**
 * Rotate the encrypted target-API credential(s) on a server.
 *
 * Pass only the field(s) being rotated â€” omitted fields leave the
 * existing ciphertext untouched.  Empty string clears the credential.
 *
 * Takes effect on the next ``regenerateServer(id)``.  The plaintext
 * is encrypted at rest with Fernet and never echoed in the response.
 *
 * Backend gates: requires EDITOR role on the server's org (or direct
 * ownership for personal servers).
 */
export function rotateServerCredentials(
    id: string, patch: ServerCredentialsPatch,
): Promise<ServerSummary> {
    return request(`/servers/${id}/credentials`, {
        method: "PATCH",
        body: JSON.stringify(patch),
    });
}

export interface BearerTokenRotateResponse {
    bearer_token: string;        // plaintext, shown ONCE
    rotated_at: string;
    regenerate_required: boolean;
    previous_bearer_accepted_until_regenerate: boolean;
    dependent_gateway_routes: number;
    dependent_webhooks: number;
    dependent_scheduled_triggers: number;
}

// ---------- Spec revisions (history + rollback) ----------

export interface SpecRevision {
    id: string;
    sha256: string;
    filename_hint: string;
    bytes_size: number;
    uploaded_at: string;
}

/** List recent spec revisions for a server, newest first.  Capped
 *  at 5 retention slots by the pipeline. */
export function listSpecRevisions(serverId: string, signal?: AbortSignal): Promise<SpecRevision[]> {
    return request(`/servers/${serverId}/spec/revisions`, { signal });
}

/** Download URL for one revision â€” used as `<a href>` so the
 *  browser handles the file save dialog. */
export function specRevisionDownloadUrl(serverId: string, revId: string): string {
    return `${API_BASE}/servers/${serverId}/spec/revisions/${encodeURIComponent(revId)}/download`;
}

/** Roll back to a previous spec revision and trigger a regenerate.
 *  Returns 409 if a regenerate is already in flight. */
export function rollbackSpecRevision(serverId: string, revId: string): Promise<ServerSummary> {
    return request(
        `/servers/${serverId}/spec/revisions/${encodeURIComponent(revId)}/rollback`,
        { method: "POST" },
    );
}


/** Rotate the MCP bearer token for a server.  Returns the new plaintext
 *  exactly once â€” show + offer-copy in the UI; DB stores only the hash.
 *  ADMIN role required.  The customer must regenerate the server
 *  afterward for their running container to pick up the new hash, but
 *  both old and new tokens validate during the in-between window. */
export function rotateServerBearerToken(id: string): Promise<BearerTokenRotateResponse> {
    return request(`/servers/${id}/bearer-token/rotate`, { method: "POST" });
}

export interface ServerLogsResponse {
    server_id: string;
    lines: string[];
    captured_at: string;
}

/** Tail the last N lines (default 100, max 1000) of the container's
 *  stdout+stderr.  Pre-fix the dashboard had no logs surface â€” every
 *  ``output_status: "upstream_error"`` was opaque to the customer. */
export function getServerLogs(id: string, lines = 200, signal?: AbortSignal): Promise<ServerLogsResponse> {
    return request(`/servers/${id}/logs?lines=${lines}`, { signal });
}

/** Manually pause a running server (audit pass 3 finding E6).  EDITOR
 *  role.  Idempotent.  Server must be in status=RUNNING. */
export function pauseServer(id: string): Promise<ServerSummary> {
    return request(`/servers/${id}/pause`, { method: "POST" });
}

/** Resume a paused server.  EDITOR role.  Idempotent. */
export function resumeServer(id: string): Promise<ServerSummary> {
    return request(`/servers/${id}/resume`, { method: "POST" });
}

export function updateServerSettings(
    id: string,
    patch: {
        ui_enabled?: boolean;
        public?: boolean;
        public_description?: string | null;
        public_tags?: string | null;
        public_display_owner?: string | null;
        fixtures_mode?: "off" | "on";
        tool_mode?: ToolMode;
        placement_preference?: "auto" | "required_isolation";
        /** Move the server between orgs.  ``null`` / empty string makes
         *  it personal.  Any non-empty value must be an org the caller
         *  is an ADMIN of (verified server-side).  Pre-MVP-finish-line
         *  this required delete + recreate. */
        org_id?: string | null;
    },
): Promise<ServerSummary> {
    return request(`/servers/${id}/settings`, {
        method: "PATCH",
        body: JSON.stringify(patch),
    });
}

// ---------- Tool fixtures (mock-response feature) ----------

export interface ToolFixture {
    id: string;
    server_id: string;
    op_id: string;
    response_status: number;
    response_body: unknown;
    latency_ms: number;
    enabled: boolean;
    created_at: string;
    updated_at: string;
}

export interface ToolFixtureCreate {
    op_id: string;
    response_status?: number;
    response_body: unknown;
    latency_ms?: number;
    enabled?: boolean;
}

export interface ToolFixtureUpdate {
    response_status?: number;
    response_body?: unknown;
    latency_ms?: number;
    enabled?: boolean;
}

export function listToolFixtures(serverId: string, signal?: AbortSignal): Promise<ToolFixture[]> {
    return request(`/servers/${serverId}/fixtures`, { signal });
}

export function createToolFixture(
    serverId: string, body: ToolFixtureCreate,
): Promise<ToolFixture> {
    return request(`/servers/${serverId}/fixtures`, {
        method: "POST", body: JSON.stringify(body),
    });
}

export function updateToolFixture(
    serverId: string, fixtureId: string, body: ToolFixtureUpdate,
): Promise<ToolFixture> {
    return request(`/servers/${serverId}/fixtures/${fixtureId}`, {
        method: "PATCH", body: JSON.stringify(body),
    });
}

export function deleteToolFixture(
    serverId: string, fixtureId: string,
): Promise<void> {
    return request(`/servers/${serverId}/fixtures/${fixtureId}`, {
        method: "DELETE",
    });
}

// ---------- Public catalogue (Feature #4) ----------

export interface CatalogueEntry {
    slug: string;
    display_name: string;
    public_description: string | null;
    public_tags: string[];
    public_display_owner: string | null;
    tool_count: number;
    public_view_count: number;
    created_at: string;
}

export interface CatalogueDetail {
    slug: string;
    display_name: string;
    public_description: string | null;
    public_tags: string[];
    public_display_owner: string | null;
    tools: Array<{
        op_id: string;
        display_name: string | null;
        description?: string | null;
        kind: "openapi" | "custom";
    }>;
    prompts: Array<{ name: string; description: string | null }>;
    resources: Array<{
        uri: string;
        name: string;
        description: string | null;
    }>;
    public_view_count: number;
    created_at: string;
}

export interface CataloguePage {
    items: CatalogueEntry[];
    total: number;
    page: number;
    per_page: number;
}

export function listCatalogue(params: {
    q?: string;
    tag?: string;
    page?: number;
    per_page?: number;
} = {}): Promise<CataloguePage> {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.tag) qs.set("tag", params.tag);
    if (params.page) qs.set("page", String(params.page));
    if (params.per_page) qs.set("per_page", String(params.per_page));
    const suffix = qs.toString();
    return request(`/catalogue${suffix ? `?${suffix}` : ""}`, { anonymous: true });
}

export function getCatalogueDetail(slug: string): Promise<CatalogueDetail> {
    return request(`/catalogue/${encodeURIComponent(slug)}`, { anonymous: true });
}

export function forkServer(id: string): Promise<ServerCreated> {
    return request(`/servers/${id}/fork`, { method: "POST" });
}

export function forkByCatalogueSlug(slug: string): Promise<ServerCreated> {
    return request(`/catalogue/${encodeURIComponent(slug)}/fork`, { method: "POST" });
}

// ---------- Tool configs ----------

export interface ToolConfig {
    op_id: string;
    /** Canonical compiled operation method; absent for non-OpenAPI/older sources. */
    http_method?: string | null;
    display_name: string | null;
    enabled: boolean;
    call_count: number;
    error_count: number;
    total_latency_ms: number;
    observed_latency_samples?: number | null;
    avg_observed_latency_ms?: number | null;
    last_called_at: string | null;
    input_schema_strict: boolean;
    output_schema_strict: boolean;
    has_output_schema: boolean;
    description_hash: string | null;
    description_tampered: boolean;
    required_scope: string | null;
    required_provider: string | null;
    effectiveness_score: number | null;
    effectiveness_metric_definition?: string | null;
    labeled_sessions?: number | null;
    successful_sessions?: number | null;
    input_schema: Record<string, unknown> | null;
}

export interface ToolTestInvokeRequest {
    input: Record<string, unknown>;
    bearer_token: string;
}

export interface ToolTestInvokeResponse {
    tool_name: string;
    output_status: "ok" | "upstream_error" | "network_error";
    latency_ms: number;
    input_hash: string;
    response_excerpt: string;
    rpc_error: string | null;
    audit_log_id: string;
}

export function testInvokeTool(
    serverId: string,
    opId: string,
    body: ToolTestInvokeRequest,
    signal?: AbortSignal,
): Promise<ToolTestInvokeResponse> {
    return request(`/servers/${serverId}/tools/${encodeURIComponent(opId)}/test-invoke`, {
        method: "POST",
        body: JSON.stringify(body),
        signal,
    });
}

export interface ToolPatch {
    enabled?: boolean;
    input_schema_strict?: boolean;
    output_schema_strict?: boolean;
    required_scope?: string;
    // DownstreamOAuthProvider.id; empty string clears the binding.
    required_provider?: string;
}

export function listTools(serverId: string, signal?: AbortSignal): Promise<ToolConfig[]> {
    return request(`/servers/${serverId}/tools`, { signal });
}

// ---------- Downstream OAuth (URL-elicitation) ----------

export type DownstreamProviderName =
    | "google"
    | "slack"
    | "github"
    | "notion"
    | "microsoft"
    | "custom";

export interface DownstreamOAuthProvider {
    id: string;
    server_id: string;
    provider_name: DownstreamProviderName;
    display_name: string;
    client_id: string;
    authorize_url: string;
    token_url: string;
    refresh_url: string | null;
    scopes: string;
    created_at: string;
}

export interface DownstreamEndUserToken {
    id: string;
    end_user_id: string;
    provider_id: string;
    provider_name: string;
    expires_at: string | null;
    scopes_granted: string | null;
    created_at: string;
    last_used_at: string | null;
}

export function listDownstreamProviders(
    serverId: string,
    signal?: AbortSignal,
): Promise<DownstreamOAuthProvider[]> {
    return request(`/servers/${serverId}/downstream-oauth`, { signal });
}

export interface DownstreamProviderCreate {
    provider_name: DownstreamProviderName;
    display_name: string;
    client_id: string;
    client_secret: string;
    authorize_url?: string;
    token_url?: string;
    refresh_url?: string;
    scopes?: string;
    extra_params_json?: string;
}

export function createDownstreamProvider(
    serverId: string,
    body: DownstreamProviderCreate,
): Promise<DownstreamOAuthProvider> {
    return request(`/servers/${serverId}/downstream-oauth`, {
        method: "POST",
        body: JSON.stringify(body),
    });
}

export function deleteDownstreamProvider(
    serverId: string,
    providerId: string,
): Promise<void> {
    return request(`/servers/${serverId}/downstream-oauth/${providerId}`, {
        method: "DELETE",
    });
}

export function listDownstreamTokens(
    serverId: string,
    providerId: string,
    signal?: AbortSignal,
): Promise<DownstreamEndUserToken[]> {
    return request(
        `/servers/${serverId}/downstream-oauth/${providerId}/tokens`,
        { signal },
    );
}

export function revokeDownstreamToken(
    serverId: string,
    providerId: string,
    tokenId: string,
): Promise<void> {
    return request(
        `/servers/${serverId}/downstream-oauth/${providerId}/tokens/${tokenId}`,
        { method: "DELETE" },
    );
}

export function toggleTool(
    serverId: string,
    opId: string,
    enabled: boolean,
): Promise<ToolConfig> {
    return request(`/servers/${serverId}/tools/${opId}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled }),
    });
}

export function patchTool(
    serverId: string,
    opId: string,
    patch: ToolPatch,
): Promise<ToolConfig> {
    return request(`/servers/${serverId}/tools/${opId}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
    });
}

// ---------- Server Prompts ----------

export interface ServerPrompt {
    id: string;
    name: string;
    description: string | null;
    template: string;
    arguments: string[];
    created_at: string;
    updated_at: string;
}

export function listPrompts(serverId: string, signal?: AbortSignal): Promise<ServerPrompt[]> {
    return request(`/servers/${serverId}/prompts`, { signal });
}

export function createPrompt(
    serverId: string,
    params: { name: string; description?: string; template: string },
): Promise<ServerPrompt> {
    return request(`/servers/${serverId}/prompts`, {
        method: "POST",
        body: JSON.stringify(params),
    });
}

export function updatePrompt(
    serverId: string,
    promptId: string,
    params: { description?: string; template: string },
): Promise<ServerPrompt> {
    return request(`/servers/${serverId}/prompts/${promptId}`, {
        method: "PATCH",
        body: JSON.stringify(params),
    });
}

export function deletePrompt(serverId: string, promptId: string): Promise<void> {
    return request(`/servers/${serverId}/prompts/${promptId}`, { method: "DELETE" });
}

// ---------- Server Resources ----------

export interface ServerResource {
    id: string;
    uri: string;
    name: string;
    description: string | null;
    mime_type: string;
    content_text: string | null;
    source_url: string | null;
    created_at: string;
    updated_at: string;
}

export interface ResourceInput {
    uri: string;
    name: string;
    description?: string | null;
    mime_type?: string;
    content_text?: string | null;
    source_url?: string | null;
}

export function listResources(serverId: string, signal?: AbortSignal): Promise<ServerResource[]> {
    return request(`/servers/${serverId}/resources`, { signal });
}

export function createResource(
    serverId: string,
    params: ResourceInput,
): Promise<ServerResource> {
    return request(`/servers/${serverId}/resources`, {
        method: "POST",
        body: JSON.stringify(params),
    });
}

export function updateResource(
    serverId: string,
    resourceId: string,
    params: Omit<ResourceInput, "uri">,
): Promise<ServerResource> {
    return request(`/servers/${serverId}/resources/${resourceId}`, {
        method: "PATCH",
        body: JSON.stringify(params),
    });
}

export function deleteResource(
    serverId: string,
    resourceId: string,
): Promise<void> {
    return request(`/servers/${serverId}/resources/${resourceId}`, {
        method: "DELETE",
    });
}

// ---------- Studio A: Custom Tools ----------

export interface CustomTool {
    id: string;
    server_id: string;
    name: string;
    description: string;
    input_schema: Record<string, unknown>;
    code: string;
    enabled: boolean;
    created_at: string;
    updated_at: string;
    // Warn-level safety findings from the pre-publish gate on the last
    // save.  Empty on a clean tool; populated when the user chose to save
    // code that tripped a non-blocking warning (e.g. ``import pickle``).
    // Error-level findings never reach this list â€” they block the save
    // with HTTP 400 before the tool is persisted.
    safety_findings?: AuthoringSafetyFinding[];
}

export interface CustomToolCreate {
    name: string;
    description: string;
    input_schema?: Record<string, unknown>;
    code: string;
}

export interface CustomToolUpdate {
    description?: string;
    input_schema?: Record<string, unknown>;
    code?: string;
    enabled?: boolean;
}

export function listCustomTools(serverId: string, signal?: AbortSignal): Promise<CustomTool[]> {
    return request(`/servers/${serverId}/custom-tools`, { signal });
}

export function createCustomTool(
    serverId: string,
    params: CustomToolCreate,
): Promise<CustomTool> {
    return request(`/servers/${serverId}/custom-tools`, {
        method: "POST",
        body: JSON.stringify(params),
    });
}

export function updateCustomTool(
    serverId: string,
    toolId: string,
    params: CustomToolUpdate,
): Promise<CustomTool> {
    return request(`/servers/${serverId}/custom-tools/${toolId}`, {
        method: "PATCH",
        body: JSON.stringify(params),
    });
}

export function deleteCustomTool(
    serverId: string,
    toolId: string,
): Promise<void> {
    return request(`/servers/${serverId}/custom-tools/${toolId}`, {
        method: "DELETE",
    });
}

// ---------- Studio B: NL authoring ----------

export interface AuthoringSafetyFinding {
    level: "warn" | "error";
    kind: string;
    message: string;
    line: number | null;
}

export interface AuthoringProposed {
    name?: string;
    description?: string;
    input_schema?: Record<string, unknown>;
    code?: string;
}

export interface AuthoringTurnResponse {
    conversation_id: string;
    reply: string;
    proposed: AuthoringProposed | null;
    safety_findings: AuthoringSafetyFinding[];
}

export interface ConversationTurn {
    role: "user" | "assistant";
    content: string;
    proposed: AuthoringProposed | null;
    safety_findings: AuthoringSafetyFinding[];
}

export interface ConversationResponse {
    id: string;
    custom_tool_id: string | null;
    turns: ConversationTurn[];
    created_at: string;
    updated_at: string;
}

export function authoringTurn(
    serverId: string,
    body: {
        message: string;
        conversation_id?: string | null;
        custom_tool_id?: string | null;
    },
): Promise<AuthoringTurnResponse> {
    return request(`/servers/${serverId}/custom-tools/ai/turn`, {
        method: "POST",
        body: JSON.stringify(body),
    });
}

export function getConversation(
    serverId: string,
    conversationId: string,
): Promise<ConversationResponse> {
    return request(
        `/servers/${serverId}/custom-tools/conversations/${conversationId}`,
    );
}

export function deleteConversation(
    serverId: string,
    conversationId: string,
): Promise<void> {
    return request(
        `/servers/${serverId}/custom-tools/conversations/${conversationId}`,
        { method: "DELETE" },
    );
}

// ---------- Postman ----------

export interface PostmanConvertResult {
    openapi: Record<string, unknown> | null;
    issues: Array<{ level: string; code: string; message: string; hint: string }>;
    description_quality?: DescriptionQualityReport;
}

export function convertPostman(file: File): Promise<PostmanConvertResult> {
    const form = new FormData();
    form.set("collection_file", file);
    return request("/postman/convert", { method: "POST", body: form });
}

export interface SpecValidationResult {
    version: string | null;
    issues: Array<{ level: string; code: string; message: string; hint: string }>;
    description_quality?: DescriptionQualityReport;
}

export interface DescriptionQualityReport {
    score: number;
    rating: "strong" | "ok" | "weak" | "poor";
    total_operations: number;
    described_operations: number;
    strong_operations: number;
    missing_operations: string[];
    weak_operations: string[];
    suggestions: string[];
}

export function validateSpec(file: File): Promise<SpecValidationResult> {
    const form = new FormData();
    form.set("spec_file", file);
    return request("/specs/validate", { method: "POST", body: form });
}

// ---------- Organisations ----------

export function listOrgs(): Promise<OrgSummary[]> {
    return request("/orgs");
}

export function getOrg(id: string): Promise<OrgDetail> {
    return request(`/orgs/${id}`);
}

export function createOrg(params: { name: string; slug: string }): Promise<OrgDetail> {
    return request("/orgs", { method: "POST", body: JSON.stringify(params) });
}

export function updateOrg(id: string, name: string): Promise<OrgDetail> {
    return request(`/orgs/${id}`, { method: "PATCH", body: JSON.stringify({ name }) });
}

export function deleteOrg(id: string): Promise<void> {
    return request(`/orgs/${id}`, { method: "DELETE" });
}

export function listOrgMembers(orgId: string): Promise<OrgMember[]> {
    return request(`/orgs/${orgId}/members`);
}

export function updateMemberRole(orgId: string, customerId: string, role: OrgRole): Promise<OrgMember> {
    return request(`/orgs/${orgId}/members/${customerId}`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
    });
}

export function updateMemberScopes(
    orgId: string,
    customerId: string,
    grantedScopes: string[],
): Promise<OrgMember> {
    return request(`/orgs/${orgId}/members/${customerId}/scopes`, {
        method: "PATCH",
        body: JSON.stringify({ granted_scopes: grantedScopes }),
    });
}

export function removeOrgMember(orgId: string, customerId: string): Promise<void> {
    return request(`/orgs/${orgId}/members/${customerId}`, { method: "DELETE" });
}

export function listOrgInvites(orgId: string): Promise<OrgInvite[]> {
    return request(`/orgs/${orgId}/invites`);
}

export function createInvite(orgId: string, email: string, role: OrgRole): Promise<OrgInvite> {
    return request(`/orgs/${orgId}/invites`, {
        method: "POST",
        body: JSON.stringify({ email, role }),
    });
}

export function cancelInvite(orgId: string, inviteId: string): Promise<void> {
    return request(`/orgs/${orgId}/invites/${inviteId}`, { method: "DELETE" });
}

export function acceptInvite(token: string): Promise<OrgDetail> {
    return request("/invites/accept", {
        method: "POST",
        body: JSON.stringify({ token }),
    });
}

export function listOrgServers(orgId: string, opts?: { limit?: number; offset?: number; workspace?: string }): Promise<ServerSummary[]> {
    const q = new URLSearchParams();
    if (opts?.limit !== undefined) q.set("limit", String(opts.limit));
    if (opts?.offset !== undefined) q.set("offset", String(opts.offset));
    const qs = q.toString();
    return request(`/orgs/${orgId}/servers${qs ? `?${qs}` : ""}`);
}

// ---------- Connect snippets ----------

export interface ConnectSnippet {
    client_id: string;
    display_name: string;
    language: string;
    snippet: string;
    notes: string;
}

export interface ConnectSnippetsResponse {
    mcp_url: string;
    snippets: ConnectSnippet[];
}

export function getConnectSnippets(serverId: string, signal?: AbortSignal): Promise<ConnectSnippetsResponse> {
    return request(`/servers/${serverId}/connect-snippets`, { signal });
}

// ---------- Toolsets ----------

export interface ServerToolsetContent {
    tools: string[];
    custom_tools: string[];
    prompts: string[];
    resources: string[];
    skills: string[];
}

export interface ServerToolset extends ServerToolsetContent {
    id: string;
    server_id: string;
    slug: string;
    name: string;
    description: string | null;
    enabled: boolean;
    mcp_url: string;
    created_at: string;
    updated_at: string;
}

export interface ServerToolsetCreate extends ServerToolsetContent {
    slug: string;
    name: string;
    description?: string | null;
    enabled?: boolean;
}

export interface ServerToolsetUpdate {
    slug?: string;
    name?: string;
    description?: string | null;
    enabled?: boolean;
    tools?: string[];
    custom_tools?: string[];
    prompts?: string[];
    resources?: string[];
    skills?: string[];
}

export function listToolsets(serverId: string, signal?: AbortSignal): Promise<ServerToolset[]> {
    return request(`/servers/${serverId}/toolsets`, { signal });
}

export interface ServerToolsetOptions {
    platform_tools: string[];
    platform_resources: string[];
}

export function getToolsetOptions(serverId: string, signal?: AbortSignal): Promise<ServerToolsetOptions> {
    return request(`/servers/${serverId}/toolset-options`, { signal });
}

export function createToolset(
    serverId: string,
    params: ServerToolsetCreate,
): Promise<ServerToolset> {
    return request(`/servers/${serverId}/toolsets`, {
        method: "POST",
        body: JSON.stringify(params),
    });
}

export function updateToolset(
    serverId: string,
    toolsetId: string,
    params: ServerToolsetUpdate,
): Promise<ServerToolset> {
    return request(`/servers/${serverId}/toolsets/${toolsetId}`, {
        method: "PATCH",
        body: JSON.stringify(params),
    });
}

export function deleteToolset(serverId: string, toolsetId: string): Promise<void> {
    return request(`/servers/${serverId}/toolsets/${toolsetId}`, { method: "DELETE" });
}

export function getToolsetConnectSnippets(
    serverId: string,
    toolsetId: string,
): Promise<ConnectSnippetsResponse> {
    return request(`/servers/${serverId}/toolsets/${toolsetId}/connect-snippets`);
}


// ---------- Skills ----------

export interface Skill {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    instructions: string;
    prompts_json: string | null;
    version: string;
    created_by: string | null;
    org_id: string | null;
    created_at: string;
    updated_at: string;
    draft_generation: number;
    archived_at: string | null;
}

export interface ServerSkill {
    id: string;
    server_id: string;
    skill_id: string;
    installed_at: string;
    skill: Skill;
}

export function listSkills(signal?: AbortSignal, workspace?: string, offset = 0, project_id?: string | null, includeArchived = false): Promise<Skill[]> {
    return request(`/skills?offset=${offset}&include_archived=${includeArchived}${project_id ? `&project_id=${encodeURIComponent(project_id)}` : ""}${workspace ? `&workspace=${encodeURIComponent(workspace)}` : ""}`, { signal });
}

export function getSkill(id: string): Promise<Skill> {
    return request(`/skills/${id}`);
}

export function createSkill(params: {
    slug: string;
    name: string;
    description?: string;
    instructions: string;
    prompts_json?: string;
    version?: string;
    org_id?: string;
    project_id?: string;
}): Promise<Skill> {
    return request("/skills", { method: "POST", body: JSON.stringify(params) });
}

export function updateSkill(
    id: string,
    params: {
        name?: string;
        description?: string;
        instructions?: string;
        prompts_json?: string;
        version?: string;
        expected_generation?: number;
    },
): Promise<Skill> {
    return request(`/skills/${id}`, { method: "PATCH", body: JSON.stringify(params) });
}

export function deleteSkill(id: string): Promise<void> {
    return request(`/skills/${id}`, { method: "DELETE" });
}

export function exportSkill(id: string): Promise<Skill> {
    return request(`/skills/${id}/export`);
}

export function listServerSkills(serverId: string, signal?: AbortSignal): Promise<ServerSkill[]> {
    return request(`/servers/${serverId}/skills`, { signal });
}

export function installSkill(serverId: string, skillId: string): Promise<ServerSkill> {
    return request(`/servers/${serverId}/skills`, {
        method: "POST",
        body: JSON.stringify({ skill_id: skillId }),
    });
}

export function uninstallSkill(serverId: string, skillId: string): Promise<void> {
    return request(`/servers/${serverId}/skills/${skillId}`, { method: "DELETE" });
}

// ---------- Security (Phase 3) ----------

export interface ToolSecurityStatus {
    op_id: string;
    description_hash: string | null;
    description_tampered: boolean;
}

export interface ServerSecurityResponse {
    server_id: string;
    pinned_tool_count: number;
    tampered_tool_count: number;
    tools: ToolSecurityStatus[];
}

export function getServerSecurity(serverId: string, signal?: AbortSignal): Promise<ServerSecurityResponse> {
    return request(`/servers/${serverId}/security`, { signal });
}

export interface SecurityScoreBreakdown {
    key: string;
    label: string;
    points_awarded: number;
    points_max: number;
    detail: string;
}

export interface SecurityScoreResponse {
    server_id: string;
    score: number;
    grade: string;
    breakdown: SecurityScoreBreakdown[];
    computed_at: string;
}

export function getSecurityScore(serverId: string, signal?: AbortSignal): Promise<SecurityScoreResponse> {
    return request(`/servers/${serverId}/security/score`, { signal });
}

// ---------- Intelligence (Phase 5) ----------

export type SessionOutcome = "unknown" | "success" | "failure";

export interface AgentSession {
    id: string;
    server_id: string;
    org_id: string | null;
    user_id: string | null;
    started_at: string;
    ended_at: string | null;
    outcome: SessionOutcome;
    event_count: number;
}

export interface TraceEvent {
    timestamp: string;
    tool_name: string;
    output_status: string;
    latency_ms: number;
    input_hash: string | null;
    estimated_token_count: number | null;
    estimated_dollar_cost: number | null;
}

export interface TraceDetail {
    session: AgentSession;
    events: TraceEvent[];
}

export interface AnomalyAlert {
    id: string;
    server_id: string;
    tool_name: string;
    kind: string;
    triggered_at: string;
    metric_value: number;
    baseline_value: number;
    resolved_at: string | null;
}

export interface CostByTool {
    tool_name: string;
    calls: number;
    total_tokens: number;
    total_dollars: number;
}

export interface CostByDay {
    date: string;
    calls: number;
    total_tokens: number;
    total_dollars: number;
    // Latency percentiles per day, computed via Postgres
    // percentile_disc(0.5/0.95) WITHIN GROUP (ORDER BY latency_ms).
    // Optional for backward compat with stale backends that don't
    // emit them yet.
    p50_latency_ms?: number;
    p95_latency_ms?: number;
}

export interface CostByUser {
    user_id: string | null;
    calls: number;
    total_tokens: number;
    total_dollars: number;
}

export interface CostSummary {
    server_id: string;
    total_calls: number;
    total_tokens: number;
    total_dollars: number;
    by_tool: CostByTool[];
    by_day: CostByDay[];
    by_user: CostByUser[];
}

export function listTraces(serverId: string, limit = 200, signal?: AbortSignal): Promise<AgentSession[]> {
    return request(`/servers/${serverId}/traces?limit=${limit}`, { signal });
}

export function getTrace(serverId: string, sessionId: string, signal?: AbortSignal): Promise<TraceDetail> {
    return request(`/servers/${serverId}/traces/${sessionId}`, { signal });
}

export function listAnomalies(
    serverId: string,
    activeOnly = true,
    signal?: AbortSignal,
): Promise<AnomalyAlert[]> {
    return request(`/servers/${serverId}/anomalies?active_only=${activeOnly}`, { signal });
}

// ---------- Description optimiser proposals (Phase 8) ----------

export interface DescriptionOptimisation {
    id: string;
    tool_config_id: string;
    tool_name: string;
    original_description: string;
    suggested_description: string;
    improvement_reason: string | null;
    effectiveness_before: number | null;
    publication_fence: number | null;
    publication_generation: number | null;
    description_sha256: string | null;
    metric_definition: string | null;
    labeled_sessions: number | null;
    successful_sessions: number | null;
    status: "pending" | "accepted" | "rejected";
    created_at: string;
    resolved_at: string | null;
}

export function listOptimisations(
    serverId: string,
    statusFilter?: "pending" | "accepted" | "rejected",
    signal?: AbortSignal,
): Promise<DescriptionOptimisation[]> {
    const qs = statusFilter ? `?status_filter=${statusFilter}` : "";
    return request(`/servers/${serverId}/optimisations${qs}`, { signal });
}

export function acceptOptimisation(
    serverId: string, optId: string,
): Promise<DescriptionOptimisation> {
    return request(`/servers/${serverId}/optimisations/${optId}/accept`, {
        method: "POST",
    });
}

export function rejectOptimisation(
    serverId: string, optId: string,
): Promise<DescriptionOptimisation> {
    return request(`/servers/${serverId}/optimisations/${optId}/reject`, {
        method: "POST",
    });
}

export function getCostSummary(
    serverId: string,
    params: { since?: string; until?: string } = {},
    signal?: AbortSignal,
): Promise<CostSummary> {
    const q = new URLSearchParams();
    if (params.since) q.set("since", params.since);
    if (params.until) q.set("until", params.until);
    const qs = q.toString();
    return request(`/servers/${serverId}/cost-summary${qs ? `?${qs}` : ""}`, { signal });
}

// ---------- Audit Logs (Phase 3 step 4) ----------

export interface AuditLogEntry {
    id: string;
    server_id: string | null;
    org_id: string | null;
    tool_name: string;
    user_id: string | null;
    timestamp: string;
    input_hash: string | null;
    output_status: string;
    latency_ms: number;
    latency_observed?: boolean;
    revision?: number;
    ip_address: string | null;
}

export interface AuditFilters {
    tool?: string;
    user?: string;
    status?: string;
    since?: string;
    until?: string;
    limit?: number;
    format?: "csv";
}

function _auditQuery(f: AuditFilters): string {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) {
        if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
}

export function listServerAudit(serverId: string, f: AuditFilters = {}, signal?: AbortSignal): Promise<AuditLogEntry[]> {
    return request(`/servers/${serverId}/audit${_auditQuery(f)}`, { signal });
}

export function listOrgAudit(orgId: string, f: AuditFilters = {}): Promise<AuditLogEntry[]> {
    return request(`/orgs/${orgId}/audit${_auditQuery(f)}`);
}

export function serverAuditCsvUrl(serverId: string, f: AuditFilters = {}): string {
    return `${API_BASE}/servers/${serverId}/audit${_auditQuery({ ...f, format: "csv" })}`;
}

export function orgAuditCsvUrl(orgId: string, f: AuditFilters = {}): string {
    return `${API_BASE}/orgs/${orgId}/audit${_auditQuery({ ...f, format: "csv" })}`;
}

export async function downloadAuthenticated(path: string, filename: string): Promise<void> {
    const token = getToken();
    const resp = await fetch(`${API_BASE}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
        clearUnauthorizedSession(resp, token);
        const detail = await resp.text().catch(() => "");
        throw new ApiError(resp.status, detail || resp.statusText);
    }
    const blob = await resp.blob();
    if (getToken() !== token) throw new ApiError(409, "Your account changed. Request the download again.");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadServerAudit(
    serverId: string,
    f: AuditFilters = {},
): Promise<void> {
    return downloadAuthenticated(
        `/servers/${serverId}/audit${_auditQuery({ ...f, format: "csv" })}`,
        `audit-${serverId}.csv`,
    );
}

export function downloadSpecRevision(
    serverId: string,
    rev: SpecRevision,
): Promise<void> {
    const safeName = rev.filename_hint.replace(/[\\/:*?"<>|]+/g, "_") || "spec";
    return downloadAuthenticated(
        `/servers/${serverId}/spec/revisions/${encodeURIComponent(rev.id)}/download`,
        safeName,
    );
}

/**
 * Open a Server-Sent Events stream of new audit rows for a server.
 *
 * Returns the raw fetch ``Response`` (not parsed) so the caller can
 * read SSE frames off ``response.body``.  We use ``fetch`` rather than
 * the browser's ``EventSource`` because EventSource doesn't allow
 * custom headers â€” we need ``Authorization: Bearer <jwt>`` to scope
 * the stream to the current customer.  ``signal`` is wired to an
 * AbortController so the caller can cancel the stream cleanly on
 * component unmount.
 */
export async function streamServerAudit(
    serverId: string, signal: AbortSignal,
): Promise<Response> {
    const headers = new Headers();
    const token = getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    headers.set("Accept", "text/event-stream");
    const res = await fetch(`${API_BASE}/servers/${serverId}/audit/stream`, {
        headers, signal, cache: "no-store",
    });
    if (!res.ok) {
        clearUnauthorizedSession(res, token);
        throw new ApiError(res.status, res.statusText);
    }
    return res;
}

// ---------- Audit log replay ----------

export interface AuditReplayRequest {
    input: Record<string, unknown>;
    bearer_token: string;
}

export interface AuditReplayOld {
    timestamp: string;
    output_status: string;
    latency_ms: number;
    input_hash: string | null;
}

export interface AuditReplayNew {
    output_status: string;
    latency_ms: number;
    input_hash: string;
    response_excerpt: string;
    rpc_error: string | null;
}

export interface AuditReplayResponse {
    tool_name: string;
    old: AuditReplayOld;
    new: AuditReplayNew;
    input_match: boolean;
}

export function replayAuditLog(
    serverId: string,
    logId: string,
    body: AuditReplayRequest,
): Promise<AuditReplayResponse> {
    return request(`/servers/${serverId}/audit/${logId}/replay`, {
        method: "POST",
        body: JSON.stringify(body),
    });
}

// ---------- Regenerate preview ----------

export interface RegenerateDiffSection {
    added: string[];
    removed: string[];
    changed: string[];
}

export interface OpSignaturePublic {
    changes?: string[];
    details?: import('./api/sourceChanges').ChangeDetail[];
    op_id: string;
    method: string;
    path: string;
    has_body: boolean;
}

export interface CustomToolBreakage {
    tool_id: string;
    tool_name: string;
    /** Human-readable list, e.g. ``["GET /removed/{id} (line 42)"]``. */
    broken_calls: string[];
}

export interface PendingSpecDiff {
    pending_uploaded_at: string | null;
    added: OpSignaturePublic[];
    removed: OpSignaturePublic[];
    changed: OpSignaturePublic[];
    description_changed?: OpSignaturePublic[];
    custom_tool_warnings: CustomToolBreakage[];
}

export interface RegeneratePreviewResponse {
    review_digest: string;
    has_previous_deploy: boolean;
    spec_changed: boolean;
    openapi_tools: RegenerateDiffSection;
    custom_tools: RegenerateDiffSection;
    prompts: RegenerateDiffSection;
    resources: RegenerateDiffSection;
    env_vars: RegenerateDiffSection;
    is_empty: boolean;
    /** Populated only when a customer has uploaded a new spec via
     * ``uploadPendingSpec`` and not yet confirmed regenerate.  Carries
     * spec-vs-spec op diff plus any custom tool that references a
     * path the new spec drops. */
    pending_spec_diff: PendingSpecDiff | null;
}

export function previewRegenerate(serverId: string): Promise<RegeneratePreviewResponse> {
    return request(`/servers/${serverId}/regenerate/preview`);
}

// ---------- Spec update flow ----------

export interface SpecUploadResponse {
    server_id: string;
    pending_uploaded_at: string | null;
    diff: PendingSpecDiff;
}

/**
 * Upload a new OpenAPI spec for review without disturbing the live
 * container.  The customer's running server keeps serving on the OLD
 * spec until they confirm via ``regenerateServer(id)``.  The diff is
 * returned synchronously so the dashboard can render the review
 * drawer without a second round-trip.
 */
export function uploadPendingSpec(
    serverId: string,
    spec: File,
): Promise<SpecUploadResponse> {
    const form = new FormData();
    form.set("spec", spec);
    return request(`/servers/${serverId}/spec`, { method: "PUT", body: form });
}

/**
 * Discard a pending spec upload without regenerating.  Idempotent â€”
 * 204 even if there's nothing to discard.
 */
export function discardPendingSpec(serverId: string): Promise<void> {
    return request(`/servers/${serverId}/spec/pending`, { method: "DELETE" });
}

// ---------- Phase 6 types: Gateways, Webhooks, Cache/Rate limit ----------

export type GatewayRoutingStrategy = "first_match" | "round_robin" | "all";

export interface Gateway {
    id: string;
    org_id: string;
    name: string;
    slug: string;
    routing_strategy: GatewayRoutingStrategy;
    endpoint_url: string;
    route_count: number;
    created_at: string;
    updated_at: string;
}

export interface GatewayCreated extends Gateway {
    bearer_token: string;
}

export interface GatewayRoute {
    id: string;
    gateway_id: string;
    server_id: string;
    priority: number;
    tool_name_pattern: string | null;
    enabled: boolean;
    created_at: string;
}

export function listGateways(orgId: string): Promise<Gateway[]> {
    return request(`/orgs/${orgId}/gateways`);
}

export function createGateway(
    orgId: string,
    params: { name: string; slug: string; routing_strategy?: GatewayRoutingStrategy },
): Promise<GatewayCreated> {
    return request(`/orgs/${orgId}/gateways`, {
        method: "POST",
        body: JSON.stringify(params),
    });
}

export function deleteGateway(orgId: string, gatewayId: string): Promise<void> {
    return request(`/orgs/${orgId}/gateways/${gatewayId}`, { method: "DELETE" });
}

export function listGatewayRoutes(orgId: string, gatewayId: string): Promise<GatewayRoute[]> {
    return request(`/orgs/${orgId}/gateways/${gatewayId}/routes`);
}

export function createGatewayRoute(
    orgId: string,
    gatewayId: string,
    params: {
        server_id: string;
        target_bearer: string;
        priority?: number;
        tool_name_pattern?: string | null;
        enabled?: boolean;
    },
): Promise<GatewayRoute> {
    return request(`/orgs/${orgId}/gateways/${gatewayId}/routes`, {
        method: "POST",
        body: JSON.stringify(params),
    });
}

export function updateGatewayRoute(
    orgId: string,
    gatewayId: string,
    routeId: string,
    params: {
        target_bearer?: string;
        priority?: number;
        tool_name_pattern?: string | null;
        enabled?: boolean;
    },
): Promise<GatewayRoute> {
    return request(`/orgs/${orgId}/gateways/${gatewayId}/routes/${routeId}`, {
        method: "PATCH",
        body: JSON.stringify(params),
    });
}

export function deleteGatewayRoute(
    orgId: string,
    gatewayId: string,
    routeId: string,
): Promise<void> {
    return request(`/orgs/${orgId}/gateways/${gatewayId}/routes/${routeId}`, {
        method: "DELETE",
    });
}

// --- Webhooks ---

export interface Webhook {
    id: string;
    server_id: string;
    tool_name: string;
    source_label: string;
    trigger_url: string;
    created_at: string;
    last_triggered_at: string | null;
    trigger_count: number;
}

export interface WebhookCreated extends Webhook {
    secret: string;
}

export interface WebhookInvocation {
    id: string;
    webhook_id: string;
    triggered_at: string;
    payload_hash: string | null;
    status: string;
    result_text: string | null;
    error_message: string | null;
}

export function listWebhooks(serverId: string, signal?: AbortSignal): Promise<Webhook[]> {
    return request(`/servers/${serverId}/webhooks`, { signal });
}

export function createWebhook(
    serverId: string,
    params: { tool_name: string; target_bearer: string; source_label?: string },
): Promise<WebhookCreated> {
    return request(`/servers/${serverId}/webhooks`, {
        method: "POST",
        body: JSON.stringify(params),
    });
}

export function updateWebhook(
    serverId: string,
    webhookId: string,
    params: { tool_name?: string; target_bearer?: string; source_label?: string },
): Promise<Webhook> {
    return request(`/servers/${serverId}/webhooks/${webhookId}`, {
        method: "PATCH",
        body: JSON.stringify(params),
    });
}

export function deleteWebhook(serverId: string, webhookId: string): Promise<void> {
    return request(`/servers/${serverId}/webhooks/${webhookId}`, { method: "DELETE" });
}

export function listWebhookInvocations(
    serverId: string,
    webhookId: string,
    limit = 100,
    signal?: AbortSignal,
): Promise<WebhookInvocation[]> {
    return request(
        `/servers/${serverId}/webhooks/${webhookId}/invocations?limit=${limit}`,
        { signal },
    );
}

// --- Cache + Rate limit ---

export interface CacheConfig {
    id: string;
    server_id: string;
    tool_name_pattern: string;
    ttl_seconds: number;
    enabled: boolean;
}

export interface RateLimitConfig {
    id: string;
    server_id: string;
    tool_name_pattern: string;
    max_calls_per_minute: number;
    enabled: boolean;
}

export interface CacheStats {
    server_id: string;
    hit_count: number;
    miss_count: number;
    hit_rate: number;
    entry_count: number;
    top_cached_tools: Array<{ tool_name: string; hits: number }>;
    process_local: boolean;
    available: boolean;
    reported_at: string | null;
    freshness: "unavailable" | "stale" | "fresh";
    snapshot_available: boolean;
}

export function listCacheConfigs(serverId: string, signal?: AbortSignal): Promise<CacheConfig[]> {
    return request(`/servers/${serverId}/cache-configs`, { signal });
}

export function createCacheConfig(
    serverId: string,
    params: { tool_name_pattern: string; ttl_seconds: number; enabled?: boolean },
): Promise<CacheConfig> {
    return request(`/servers/${serverId}/cache-configs`, {
        method: "POST",
        body: JSON.stringify(params),
    });
}

export function deleteCacheConfig(serverId: string, ruleId: string): Promise<void> {
    return request(`/servers/${serverId}/cache-configs/${ruleId}`, { method: "DELETE" });
}

export function listRateLimitConfigs(serverId: string, signal?: AbortSignal): Promise<RateLimitConfig[]> {
    return request(`/servers/${serverId}/rate-limit-configs`, { signal });
}

export function createRateLimitConfig(
    serverId: string,
    params: { tool_name_pattern: string; max_calls_per_minute: number; enabled?: boolean },
): Promise<RateLimitConfig> {
    return request(`/servers/${serverId}/rate-limit-configs`, {
        method: "POST",
        body: JSON.stringify(params),
    });
}

export function deleteRateLimitConfig(serverId: string, ruleId: string): Promise<void> {
    return request(`/servers/${serverId}/rate-limit-configs/${ruleId}`, { method: "DELETE" });
}

export function getCacheStats(serverId: string, signal?: AbortSignal): Promise<CacheStats> {
    return request(`/servers/${serverId}/cache-stats`, { signal });
}

// ---------- Inbound OAuth 2.1 (agent clients) ----------

export interface InboundOAuthClient {
    id: string;
    client_id: string;
    client_name: string;
    redirect_uris: string[];
    grant_types: string[];
    scope: string;
    token_endpoint_auth_method: string;
    logo_uri: string | null;
    client_uri: string | null;
    software_id: string | null;
    software_version: string | null;
    first_authorized_by: string | null;
    created_at: string;
    revoked_at: string | null;
    active_refresh_token_count: number;
}

export function listInboundOAuthClients(
    serverId: string,
    signal?: AbortSignal,
): Promise<InboundOAuthClient[]> {
    return request(`/servers/${serverId}/oauth/clients`, { signal });
}

export function revokeInboundOAuthClient(
    serverId: string,
    clientRowId: string,
): Promise<void> {
    return request(`/servers/${serverId}/oauth/clients/${clientRowId}`, {
        method: "DELETE",
    });
}

// ---------- Feature #5: SOC 2 + SAML SSO + MFA ----------

export interface MFAStatus {
    enrolled: boolean;
    method: string | null;
    enrolled_at: string | null;
    recovery_codes_remaining: number;
}

export interface MFAEnrollStartResponse {
    otpauth_uri: string;
    secret: string;
    qr_svg: string;
    recovery_codes: string[];
}

export function getMfaStatus(): Promise<MFAStatus> {
    return request("/auth/mfa");
}

export function startMfaEnrollment(
    tokenOverride?: string,
): Promise<MFAEnrollStartResponse> {
    return request("/auth/mfa/enroll", { method: "POST", tokenOverride });
}

export function verifyMfaEnrollment(
    code: string,
    tokenOverride?: string,
): Promise<MFAStatus> {
    return request("/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify({ code }),
        tokenOverride,
    });
}

export function unenrollMfa(code: string): Promise<void> {
    return request("/auth/mfa", {
        method: "DELETE",
        body: JSON.stringify({ code }),
    });
}

export interface SAMLConfigResponse {
    configured: boolean;
    enabled: boolean;
    idp_entity_id: string | null;
    idp_sso_url: string | null;
    cert_fingerprint: string | null;
    sp_entity_id: string | null;
    sp_acs_url: string | null;
    auto_provision_enabled: boolean;
    allowed_email_domains: string | null;
    updated_at: string | null;
}

export interface SAMLConfigRequest {
    idp_metadata_xml?: string;
    idp_entity_id?: string;
    idp_sso_url?: string;
    idp_x509_cert?: string;
    // Omit to leave the persisted value unchanged.
    enabled?: boolean;
    auto_provision_enabled?: boolean;
    // CSV of allowed domains. Empty string clears the allow-list.
    // Omit the field entirely to leave it unchanged.
    allowed_email_domains?: string;
}

export function getOrgSaml(orgId: string): Promise<SAMLConfigResponse> {
    return request(`/orgs/${orgId}/saml`);
}

export function upsertOrgSaml(
    orgId: string,
    body: SAMLConfigRequest,
): Promise<SAMLConfigResponse> {
    return request(`/orgs/${orgId}/saml`, {
        method: "PUT",
        body: JSON.stringify(body),
    });
}

export function deleteOrgSaml(orgId: string): Promise<void> {
    return request(`/orgs/${orgId}/saml`, { method: "DELETE" });
}

export interface AccessReviewMemberRow {
    customer_id: string;
    email: string;
    role: OrgRole;
    joined_at: string;
    mfa_enrolled: boolean;
    last_audit_event_at: string | null;
    granted_scope_count: number;
}

export interface AccessReviewResponse {
    org_id: string;
    org_name: string;
    generated_at: string;
    mfa_required: boolean;
    saml_required: boolean;
    members: AccessReviewMemberRow[];
}

export function getOrgAccessReview(orgId: string): Promise<AccessReviewResponse> {
    return request(`/orgs/${orgId}/access-review`);
}

// Fetches the streaming audit export with the Bearer token attached,
// then triggers a browser download via a blob URL.  An <a href> on its
// own WON'T work â€” the browser attaches no Authorization header, so the
// request would 401 at the backend.
export async function downloadOrgAudit(
    orgId: string,
    format: "jsonl" | "csv" = "jsonl",
): Promise<void> {
    return downloadAuthenticated(
        `/orgs/${orgId}/audit/export?format=${format}`,
        `audit-${orgId}.${format}`,
    );
}

export interface OrgPatchRequest {
    name: string;
    data_residency?: string;
    mfa_required?: boolean;
    saml_required?: boolean;
}

export function updateOrgCompliance(
    orgId: string,
    body: OrgPatchRequest,
): Promise<{ id: string; name: string; data_residency: string; mfa_required: boolean; saml_required: boolean }> {
    return request(`/orgs/${orgId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
    });
}
